import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, CheckCircle2, LogIn, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, floorLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { dimPairLabel, kmToMiles } from "@/lib/units";

/**
 * Save-search alert dialog (Stage 4 — account-gated).
 *
 * Creating a stock alert now REQUIRES a signed-in, email-verified customer
 * (Google counts as verified). Enforced server-side too. The alert always
 * binds to the account's own email — no free-text email entry.
 */
export default function SaveSearchButton({ filters, className = "", compact = false }) {
  const { user, resendVerification } = useAuth();
  const { unit } = useUnit();
  const nav = useNavigate();
  const verified = user && user.role === "customer" && user.email_verified !== false;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [state, setState] = useState({ kind: "idle", message: "" });

  function goSignIn() {
    const next = window.location.pathname + window.location.search;
    setOpen(false);
    nav(`/account?next=${encodeURIComponent(next)}`);
  }

  async function handleResend() {
    setResending(true);
    const res = await resendVerification();
    setResending(false);
    if (res.ok) toast.success("Verification email sent");
    else toast.error(res.error || "Couldn't send email");
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setState({ kind: "idle", message: "" });
    try {
      // No email field — the alert binds to the signed-in account's own email.
      const r = await api.post("/alerts/subscribe", { ...(filters || {}) });
      setState({
        kind: "ok",
        message: r.data.already_subscribed
          ? "You're already subscribed — this alert is in your account."
          : "Alert saved to your account. We send a daily alert email each morning with any new matching flooring.",
      });
    } catch (err) {
      setState({ kind: "err", message: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ||
          "inline-flex items-center gap-2 h-10 px-4 rounded-full border border-slate-300 bg-white hover:border-[#E05915] hover:text-[#E05915] font-semibold text-slate-800 text-sm transition-colors"
        }
        data-testid="save-search-button"
      >
        <Bell className="w-4 h-4" aria-hidden="true" />{" "}
        {compact ? "Alert me" : "Alert me when this lands"}
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="save-search-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="font-display font-bold text-xl text-slate-900">Get an email when this lands</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Fresh Manchester surplus arrives every day — we'll email you each morning with any new matching remnants.
                </p>
              </div>
              <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            {state.kind === "ok" ? (
              <div className="bg-[#2A5A3B]/10 border border-[#2A5A3B]/20 rounded-xl p-4 flex items-start gap-3" data-testid="save-search-success">
                <CheckCircle2 className="w-5 h-5 text-[#2A5A3B] shrink-0 mt-0.5" />
                <div className="text-sm text-slate-800">{state.message}</div>
              </div>
            ) : !user ? (
              <div className="text-center py-4" data-testid="alert-signin-gate">
                <LogIn className="w-10 h-10 text-[#E05915] mx-auto" />
                <h3 className="font-display font-semibold text-lg text-slate-900 mt-3">Sign in to set up alerts</h3>
                <p className="text-sm text-slate-600 mt-2">A free, verified FloorMatch account keeps your alerts in one place. Your search is saved while you sign in.</p>
                <button onClick={goSignIn} className="mt-5 h-12 px-6 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center gap-2" data-testid="alert-signin-btn">
                  Sign in or create account
                </button>
              </div>
            ) : user.role !== "customer" ? (
              <div className="text-center py-6 text-slate-600" data-testid="alert-not-customer">
                <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
                <p className="text-sm mt-3">Stock alerts are for shoppers. You're signed in with a {user.role} account.</p>
              </div>
            ) : !verified ? (
              <div className="text-center py-4" data-testid="alert-verify-gate">
                <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
                <h3 className="font-display font-semibold text-lg text-slate-900 mt-3">Confirm your email first</h3>
                <p className="text-sm text-slate-600 mt-2">Please confirm your email address before setting up alerts. Check your inbox for the link.</p>
                <button onClick={handleResend} disabled={resending} className="mt-4 inline-flex items-center gap-2 font-semibold text-[#E05915] underline disabled:opacity-60" data-testid="alert-resend-verify">
                  {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Resend verification email
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="text-sm bg-[#2A5A3B]/5 border border-[#2A5A3B]/15 rounded-lg px-3 py-2 text-slate-700" data-testid="alert-account-note">
                  We'll email <span className="font-semibold">{user.email}</span> and keep this alert under “My stock alerts”.
                </div>

                {filters && (
                  <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="font-semibold text-slate-700 mb-1">You'll be alerted for</div>
                    <ul className="text-slate-600 space-y-0.5">
                      {filters.room_width_m && filters.room_length_m && (<li>Room {dimPairLabel(filters.room_width_m, filters.room_length_m, unit)}</li>)}
                      {filters.flooring_type && <li>Type: {floorLabel(filters.flooring_type)}</li>}
                      {filters.postcode && <li>Near {filters.postcode} · within {Math.round(kmToMiles(filters.radius_km || 25))} miles</li>}
                      {filters.max_price && <li>Under £{filters.max_price}</li>}
                      {!filters.room_width_m && !filters.postcode && !filters.flooring_type && (<li>Any new Floor-Match listing</li>)}
                    </ul>
                  </div>
                )}

                {state.kind === "err" && (
                  <div className="text-sm text-red-600" data-testid="save-search-error">{state.message}</div>
                )}

                <button type="submit" disabled={busy} className="fm-btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed" data-testid="save-search-submit">
                  {busy ? "Saving..." : "Save this alert"}
                </button>
                <p className="text-[11px] text-slate-500 text-center">We only email you when new stock matches. Unsubscribe with one click any time.</p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
